import { Box, Paper, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';

export type HilSummaryCardProps = {
  title: string;
  description: string;
  count: number;
  selected: boolean;
  onClick: () => void;
};

const cardSx = (theme: Theme, selected: boolean) => ({
  p: 2.5,
  borderRadius: 2,
  cursor: 'pointer',
  position: 'relative' as const,
  overflow: 'visible',
  border: selected
    ? `2px solid ${theme.palette.warning.main}`
    : `1px solid ${theme.palette.divider}`,
  bgcolor:
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.grey[900], 0.6)
      : theme.palette.grey[50],
  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    boxShadow:
      theme.palette.mode === 'dark'
        ? `0 2px 8px ${alpha(theme.palette.common.black, 0.35)}`
        : `0 2px 8px ${alpha(theme.palette.common.black, 0.08)}`,
  },
});

export default function HilSummaryCard({
  title,
  description,
  count,
  selected,
  onClick,
}: HilSummaryCardProps) {
  const theme = useTheme();

  return (
    <Paper elevation={0} onClick={onClick} sx={cardSx(theme, selected)}>
      <Box
        sx={{
          position: 'absolute',
          top: 14,
          right: 14,
          width: 32,
          height: 32,
          borderRadius: '50%',
          bgcolor: 'warning.main',
          color: 'warning.contrastText',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.8rem',
          fontWeight: 700,
        }}
      >
        {count}
      </Box>
      <Box sx={{ pr: 5 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {description}
        </Typography>
      </Box>
    </Paper>
  );
}
